import { useNavigate } from "react-router-dom";
import {
  Card,
  InsightCard,
  LineChart,
  ProvenanceTag,
  RankList,
  StatCard,
} from "@/ui";
import {
  insights,
  overviewConversions,
  overviewVisitors,
  signalsKpis,
  topPages,
  topSources,
  genSeries,
} from "@/mock";
import type { Kpi, Point, Row } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useSummary, useTimeseries, useSources } from "@/data/hooks";
import type { Summary } from "@/data/services";
import { PanelHeader, rangeLabel } from "../parts";
import { useSignalsQuery, matches } from "../searchContext";
import { AigenTag, DemoNote, EmptyState, SampleChip, Skeleton, panelState, timeLabel } from "../wiring";

/** Percent change vs the prior period; 0 when there's no prior baseline. */
function pctChange(cur: number, prev: number | undefined): number {
  if (prev === undefined || prev <= 0) return 0;
  return Math.round(((cur - prev) / prev) * 1000) / 10;
}
function trendOf(delta: number): "up" | "down" | "flat" {
  return delta > 0 ? "up" : delta < 0 ? "down" : "flat";
}

/** Map the live summary onto the four headline StatCards (coral = focal visits).
 *  Deltas/trend are real period-over-period change from `s.previous` (#18). */
function summaryToKpis(s: Summary): Kpi[] {
  const p = s.previous;
  const src = { source: "matomo" as const };
  const dVisits = pctChange(s.visits, p?.visits);
  const dUnique = pctChange(s.unique_visitors, p?.unique_visitors);
  const dConv = pctChange(s.conversion_rate, p?.conversion_rate);
  const dAvg = pctChange(s.avg_session_seconds, p?.avg_session_seconds);
  return [
    {
      key: "visits",
      label: "Visits",
      value: Math.round(s.visits),
      unit: "count",
      series: genSeries(1, Math.max(1, s.visits / 7), Math.max(1, s.visits / 30)),
      focal: true,
      delta: dVisits,
      trend: trendOf(dVisits),
      ...src,
    },
    {
      key: "unique",
      label: "Unique visitors",
      value: Math.round(s.unique_visitors),
      unit: "count",
      series: genSeries(2, Math.max(1, s.unique_visitors / 7), Math.max(1, s.unique_visitors / 30)),
      delta: dUnique,
      trend: trendOf(dUnique),
      ...src,
    },
    {
      key: "convrate",
      label: "Conversion rate",
      value: Math.round(s.conversion_rate * 10) / 10,
      unit: "pct",
      series: genSeries(3, Math.max(1, s.conversion_rate), 1),
      delta: dConv,
      trend: trendOf(dConv),
      ...src,
    },
    {
      key: "avgsession",
      label: "Avg. session",
      value: Math.round(s.avg_session_seconds),
      unit: "sec",
      series: genSeries(4, Math.max(1, s.avg_session_seconds / 2), 10),
      delta: dAvg,
      trend: trendOf(dAvg),
      ...src,
    },
  ];
}

export function Overview() {
  const nav = useNavigate();
  const { siteId, range, live } = useWorkspace();
  const insight = insights[0]!;
  const q = useSignalsQuery();

  const summaryQ = useSummary(siteId, range);
  const seriesQ = useTimeseries(siteId, range, "visits");
  const sourcesQ = useSources(siteId, range);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: summaryQ.isLoading || seriesQ.isLoading,
    isError: summaryQ.isError || seriesQ.isError,
    hasData: !!summaryQ.data,
  });

  if (state === "loading") {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[124px]" />
          ))}
        </div>
        <Skeleton className="h-[320px]" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[260px]" />
          <Skeleton className="h-[260px]" />
        </div>
      </div>
    );
  }

  const isLive = state === "live";
  const summary = summaryQ.data;
  const seeded = !!summary?.seeded;

  // KPIs
  const kpis: Kpi[] = isLive && summary ? summaryToKpis(summary) : signalsKpis;

  // Main chart series → Point[]
  const visitsPoints: Point[] =
    isLive && seriesQ.data?.points?.length
      ? seriesQ.data.points.map((p) => ({ t: timeLabel(p.t, range), v: p.value }))
      : overviewVisitors;
  const convPoints: Point[] | undefined = isLive ? undefined : overviewConversions;

  // Top sources → Row[]
  const sourceRows: Row[] =
    isLive && sourcesQ.data?.items?.length
      ? sourcesQ.data.items.map((it) => ({
          label: it.source ?? "(direct)",
          value: it.visits,
          pct: 0,
        }))
      : topSources;

  return (
    <div className="stagger space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.key} className="relative">
            <StatCard kpi={k} />
            {!isLive && <SampleChip className="absolute right-3 top-3" />}
          </div>
        ))}
      </div>

      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <PanelHeader
            title="Visitors over time"
            desc={`First-party visit signal, ${rangeLabel(range)}.`}
          />
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-[12px] text-muted">
              <span className="h-[2px] w-4 rounded-full bg-ink-strong" />
              Visits
            </span>
            {!isLive && (
              <span className="flex items-center gap-1.5 text-[12px] text-muted">
                <span className="h-[2px] w-4 rounded-full bg-gold" />
                Conversions
              </span>
            )}
            {isLive ? <AigenTag /> : <ProvenanceTag source="posthog" />}
            {isLive && seeded && <DemoNote />}
            {!isLive && <SampleChip />}
          </div>
        </div>
        {isLive && visitsPoints.length === 0 ? (
          <EmptyState title="No visits yet" detail="Visits will appear here once this property starts receiving traffic." />
        ) : (
          <LineChart a={visitsPoints} b={convPoints} height={240} />
        )}
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <RankList
          title="Top pages"
          rows={topPages.filter((r) => matches(q, r.label))}
          accentTop
          footer={isLive ? <SampleChip /> : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          )}
        />
        <RankList
          title="Top sources"
          rows={sourceRows.filter((r) => matches(q, r.label))}
          accentTop
          footer={
            isLive ? (
              <span className="flex items-center gap-2">
                <AigenTag />
                {seeded && <DemoNote />}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ProvenanceTag source="posthog" />
                <SampleChip />
              </span>
            )
          }
        />
      </div>

      <InsightCard
        insight={insight}
        onAsk={() => nav("/make", { state: { prompt: insight.makePrompt } })}
      />
    </div>
  );
}
